<template>
    <div>
        <referrals-status :referral="referral" :isInline="true" :description="''" :descriptionStrike="''"></referrals-status>
        <referrals-counter-component
            v-if="totalNumber"
            :currentIndex="currentIndex"
            :totalNumber="totalNumber"
        ></referrals-counter-component>
        <common-content-spacer height="medium" class="referral-notes__container" :isCenter="true">
            <span>
                <h2>{{ heading }}</h2>
            </span>

            <input
                ref="fileInput"
                type="file"
                multiple="multiple"
                style="display: none"
                accept="application/pdf,image/png,image/jpg,image/jpeg"
                @change="fileSelected($event)"
            />
            <p>
                {{ $t('pages.referrals.notes.add.patient') }}
                <b>{{ patientFullName }},</b>
                {{ $t('pages.referrals.notes.add.patient_dob') }}
                <b>{{ dateFormate(referral.patient.dateOfBirth) }}</b>
            </p>
            <p></p>
            <div class="referral-notes__upload-section">
                <button
                    v-if="!selectedFiles || !selectedFiles.length"
                    class="referral-notes__upload-btn"
                    @click="$refs.fileInput.click()"
                >
                    <common-icon name="cloud" size="gigantic"></common-icon>
                    <span>{{ $t('pages.referrals.notes.add.add_report') }}</span>
                </button>
                <common-upload-summary
                    v-else
                    :isUploading="isUploading"
                    :files="selectedFiles"
                    @remove-file="removeFile($event)"
                ></common-upload-summary>
                <common-warning v-if="showWarning" size="medium" :text="warningText"></common-warning>
                <p v-if="showLimits">{{ $t('pages.referrals.notes.add.file_limits') }}</p>
            </div>

            <common-buttons>
                <common-button
                    :label="$t('pages.referrals.notes.add.buttons.select_more')"
                    type="secondary"
                    size="large"
                    :isDisabled="!selectedFiles.length"
                    :isProcessing="isUploading"
                    @click="$refs.fileInput.click()"
                ></common-button>
                <common-button
                    v-if="!showConfirm"
                    :label="$t('pages.referrals.notes.add.buttons.send_report')"
                    type="primary"
                    size="large"
                    :isDisabled="(!selectedFiles.length || isSubmitDisabled) && !$auth.isUserPracticeDemo()"
                    :isProcessing="isUploading"
                    @click="sendFiles"
                ></common-button>
                <common-button
                    v-else
                    :label="$t('pages.referrals.notes.add.buttons.confirm')"
                    type="primary"
                    size="large"
                    :isDisabled="(!selectedFiles.length || isSubmitDisabled) && !$auth.isUserPracticeDemo()"
                    :isProcessing="isUploading"
                    @click="confirmSend"
                ></common-button>
                <common-button
                    v-if="showSkipButton"
                    :label="$t('pages.referrals.notes.add.buttons.skip')"
                    :isDisabled="isUploading"
                    type="tertiary"
                    size="large"
                    :isProcessing="isUploading"
                    @click="emitEvent({ name: 'consultation-report-skip' })"
                ></common-button>
                <common-button
                    v-if="showSkipButton"
                    :label="$t('pages.referrals.update.patient_seen.is_patient_seen_modal.buttons.skip_all')"
                    type="secondary"
                    size="large"
                    @click="emitEvent({ name: 'skip-all' })"
                ></common-button>
                <common-button
                    :label="$t('pages.referrals.update.rate_referred.buttons.back_to_referrals')"
                    type="secondary"
                    size="large"
                    @click="goBackToReferrals"
                ></common-button>
            </common-buttons>
        </common-content-spacer>
        <paywall-modal v-if="isPaywallModalOpen" @close="isPaywallModalOpen = false"></paywall-modal>
    </div>
</template>

<script>
    import ReferralsStatus from './../../../src/components/page-elements/ReferralsStatus'
    import ReferralsAPI from 'api/ReferralsAPI'
    import UploadMixin from 'mixins/UploadMixin'
    import ReferralsCounterComponent from 'components/page-elements/ReferralsCounterComponent'
    import PaywallModal from 'components/page-elements/PaywallModal'
    import { dateFormate } from '~/helpers'

    export default {
        mixins: [UploadMixin],

        components: {
            ReferralsStatus,
            ReferralsCounterComponent,
            PaywallModal,
        },

        props: {
            referral: { type: Object, required: true },
            redirectToReferral: { type: Boolean, default: true },
            showSkipButton: { type: Boolean, default: false },
            totalNumber: { type: Number, required: false, default: null },
            currentIndex: { type: Number, required: false, default: 0 },
        },

        data() {
            return {
                isPaywallModalOpen: false,
            }
        },

        computed: {
            patientFullName() {
                return `${this.referral.patient.firstName} ${this.referral.patient.lastName}`
            },

            heading() {
                if (this.isUploading) {
                    return this.$t('pages.referrals.notes.add.heading_uploading')
                } else if (this.uploadedFilesCount) {
                    return this.$t('pages.referrals.notes.add.heading_uploaded', { n: this.uploadedFilesCount })
                }
                return this.$t('pages.referrals.notes.add.heading')
            },

            showConfirm() {
                return this.selectedFiles.length > 0 && this.selectedFiles.every((file) => file.isUploaded)
            },
        },

        methods: {
            dateFormate,
            sendFiles() {
                if (this.$auth.isUserPracticeDemo()) {
                    this.isPaywallModalOpen = true
                    return false
                }
                this.isUploading = true
                this.showWarning = false
                this.showLimits = true
                this.uploadScope = 'consultation-report'
                this.prepareUploads('consultation-report')
                    .then(() => {
                        const filesForUpload = this.selectedFiles.filter((file) => !file.isUploaded)
                        if (!filesForUpload.length) {
                            this.isUploading = false
                            return
                        }
                        this.uploadFiles(filesForUpload)
                    })
                    .catch(() => {
                        this.isUploading = false
                    })
            },

            uploadFiles(files) {
                ReferralsAPI.uploadConsultationNotes(files, this.referral.id)
                    .then(() => {
                        this.isUploading = false
                        if (this.errorFilesCount) {
                            this.warningText = this.$t('pages.referrals.notes.add.warnings.error', {
                                n: this.errorFilesCount,
                            })
                            this.showWarning = true
                            this.showLimits = false
                        }
                    })
                    .catch(() => {
                        this.isUploading = false
                        this.warningText = this.$t('pages.referrals.notes.add.warnings.error', { n: 1 })
                        this.showWarning = true
                    })
            },

            confirmSend() {
                if (this.$auth.isUserPracticeDemo()) {
                    this.isPaywallModalOpen = true
                    return false
                }
                this.sendConsultationReport()
            },

            sendConsultationReport() {
                this.isUploading = true
                ReferralsAPI.sendConsultationReport(this.referral.id)
                    .then(() => {
                        this.isUploading = false
                        this.goToReferral()
                    })
                    .catch(() => {
                        this.isUploading = false
                    })
            },

            removeFile(event) {
                if (!this.isUploading) {
                    if (event.file.id) {
                        ReferralsAPI.removeConsultationReport(event.file.id).then(() => {
                            this.removeAndValidateFiles(event)
                        })
                    } else {
                        this.removeAndValidateFiles(event)
                    }
                }
            },

            fileSelected(event) {
                this.onFileSelected(event)
                this.$refs.fileInput.value = ''
            },

            goToReferral() {
                if (this.redirectToReferral) {
                    this.$router.push({ name: 'referrals.received', params: { sourceType: 'direct', id: this.referral.id } })
                } else {
                    this.$emit('consultation-report-saved')
                }
            },

            addLoadedFiles(files) {
                files.forEach((file) => {
                    this.selectedFiles.push({
                        file: {
                            id: file.id,
                            name: file.name,
                            size: file.size,
                        },
                        progress: 100,
                        isUploaded: true,
                    })
                })
            },

            confirmLeave(e) {
                if (!this.selectedFiles.length) {
                    return
                }

                e.preventDefault()
                e.returnValue = ''
            },
            goBackToReferrals() {
                this.$router.push({ name: `referrals.received` })
            },
        },

        mounted() {
            ReferralsAPI.getConsultationNotes(this.referral.id).then((files) => {
                this.addLoadedFiles(files)
            })
        },

        created() {
            window.addEventListener('beforeunload', this.confirmLeave)
        },

        beforeDestroy() {
            window.removeEventListener('beforeunload', this.confirmLeave)
        },
    }
</script>
